import { Extension } from "@tiptap/core";
import type { CommandProps } from "@tiptap/core";
import { closeHistory } from "@tiptap/pm/history";
import type { SelectionRange } from "@tiptap/pm/state";
import { Transform } from "@tiptap/pm/transform";

/**
 * Toolbar "Großbuchstaben" / "Kleinbuchstaben" (Anke, 2026-09-23): the selected
 * characters are really rewritten, like Shift+F3 in Word — NOT a CSS
 * `text-transform`. The stored document carries the new letters, so search,
 * export and copied plain text all see them, and no schema attribute is needed.
 *
 * This is deliberately a different mechanism from the whiteboard textbox's
 * `textTransform` (a reversible `textStyle` attribute rendered as CSS): the two
 * do not share code because they do not do the same thing.
 *
 * Known, accepted limits (same as Word): lowercasing `SS` does not bring back
 * `ß`, and a letter whose case depends on its neighbour (Greek final sigma) is
 * converted per text node, i.e. per mark run.
 */
export type TextCaseMode = "upper" | "lower";

function convert(text: string, mode: TextCaseMode): string {
  return mode === "upper"
    ? text.toLocaleUpperCase("de-DE")
    : text.toLocaleLowerCase("de-DE");
}

/**
 * Rewrite the text inside `ranges` to upper or lower case, keeping every mark
 * of every run (bold, colour, font size, link, highlight …). Non-text nodes
 * (images, hard breaks) are left alone. Returns whether anything changed.
 *
 * `ranges` must be positions in `tr.doc` as it is when this is called — pass
 * `tr.selection.ranges`, not the ranges of an older state. Taking every range
 * (not just `from`/`to`) is what makes a multi-cell `CellSelection` work: its
 * `from`/`to` cover only the first cell.
 *
 * No selection is set here. A `Transaction` maps its selection through the new
 * steps by itself, which keeps a `TextSelection` over the rewritten text and
 * leaves an `AllSelection`, `CellSelection` or `NodeSelection` of its own type.
 */
export function applyTextCase(
  tr: Transform,
  ranges: readonly SelectionRange[],
  mode: TextCaseMode,
): boolean {
  // Walk an immutable snapshot and map its positions through the steps THIS
  // call adds — not through steps the transform already carried, which the
  // caller's positions already account for.
  const doc = tr.doc;
  const firstOwnStep = tr.mapping.maps.length;
  // Build the slice on every call: `slice()` fixes its end at the CURRENT
  // number of maps, so a slice taken once up front would stay empty.
  const map = (pos: number): number => tr.mapping.slice(firstOwnStep).map(pos);
  let changed = false;

  for (const range of ranges) {
    const from = range.$from.pos;
    const to = range.$to.pos;
    doc.nodesBetween(from, to, (node, pos) => {
      if (!node.isText || node.text === undefined) {
        return;
      }
      const start = Math.max(pos, from);
      const end = Math.min(pos + node.nodeSize, to);
      if (start >= end) {
        return;
      }
      const original = node.text.slice(start - pos, end - pos);
      const rewritten = convert(original, mode);
      if (rewritten === original) {
        return;
      }
      // For two adjacent runs, this run's `start` equals the previous run's
      // `end`. `map(start)` still lands at the END of the previous run's new
      // text because a position on the end edge of a replaced range always
      // maps to its far side (prosemirror-transform StepMap, `pos == end`).
      // Do not "tidy" this into `map(start, -1)` — that would re-replace the
      // tail of the previous run.
      tr.replaceWith(
        map(start),
        map(end),
        doc.type.schema.text(rewritten, node.marks),
      );
      changed = true;
    });
  }
  return changed;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    textCase: {
      /** Rewrite the selected text in capital letters. */
      setUppercase: () => ReturnType;
      /** Rewrite the selected text in small letters. */
      setLowercase: () => ReturnType;
    };
  }
}

/**
 * The commands behind the two toolbar buttons. Commands only — no global
 * attributes, no node or mark schema: the editor's schema must stay identical
 * to the one `generateHTML` renders with (`RICH_TEXT_EXTENSIONS`), which is why
 * this extension is registered by the element's edit-mode editor alone and not
 * in `createRichTextExtensions()` (shared with the whiteboard and the
 * open2done converter).
 */
export const TextCase = Extension.create({
  name: "textCase",

  addCommands() {
    const run =
      (mode: TextCaseMode) =>
      () =>
      ({ tr, dispatch }: CommandProps): boolean => {
        if (!dispatch) {
          // `can()`: report whether a rewrite would change anything, on a
          // throw-away copy — tiptap hands `can()` the same transaction a chain
          // would dispatch, so it must stay untouched.
          return applyTextCase(new Transform(tr.doc), tr.selection.ranges, mode);
        }
        if (!applyTextCase(tr, tr.selection.ranges, mode)) {
          return false;
        }
        // Start a fresh undo step, so a rewrite right after typing is not
        // merged into the typing and one Ctrl+Z undoes exactly the rewrite.
        closeHistory(tr);
        return true;
      };
    return {
      setUppercase: run("upper"),
      setLowercase: run("lower"),
    };
  },
});
